import { LinkableType } from "@/components/LinksManagement";
import { toast } from "@/hooks/use-toast";
import api from "@/lib/axiosConfig";
import { ResourceType } from "@/lib/resource";
import { MultipleprintNature } from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PaginatedReturn, PagingParameters } from "./dtos";

export type RelationTable =
  | "relatedArtwork"
  | "relatedPhotography"
  | "relatedWriting"
  | "relatedAudiovisual"
  | "relatedDocument"
  | "relatedBibliography"
  | "relatedExhibition"
  | "relatedAuction"
  | "relatedAuthor"
  | "relatedMultipleprint"
  | "relatedSculptureEditions"
  | "relatedTirage"
  | "multipleprintInMultipleSample"
  | "relatedMultiplesample"
  | "relatedMultipleSample"
  | "externalLinks";

async function getRelatedFields(
  idFirstRelation: string | undefined,
  relationBase: string,
  relationTable: string,
  take: number,
  skip: number,
): Promise<any> {
  if (!idFirstRelation) {
    throw new Error("Id is required");
  }
  const { data } = await api.get(
    `back/api/related-fields/${idFirstRelation}/${relationBase}/${relationTable}?take=${take}&skip=${skip}`,
  );
  return data;
}

export const useGetRelatedFields = (
  id: string | undefined,
  type: LinkableType,
  relationBase: RelationTable,
  take: number,
  skip: number,
) => {
  return useQuery({
    queryFn: () => getRelatedFields(id, type, relationBase, take, skip),
    queryKey: [type, relationBase, id, { take, skip }],
    enabled: !!id,
  });
};

async function createRelatedFields(
  relationBase: string,
  relationTable: string,
  ids: string[],
  translationKey: string,
): Promise<any> {

  if (!ids || ids.length !== 2) {
    throw new Error("Invalid number of ids");
  }
  const { data } = await api.post(
    `back/api/related-fields/${relationBase}/${relationTable}`,
    { ids },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return data;
}

export const useCreateRelatedFields = (
  id: string | undefined,
  type: LinkableType,
  relationTable: RelationTable,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (ids: string[]) =>
      createRelatedFields(
        type,
        relationTable,
        ids,
        t(`${relationTable}UpdatedToastDescription`),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [type, relationTable, id] });
      queryClient.invalidateQueries({ queryKey: ["not-related"] });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", type],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

async function deleteRelation(
  relationBase: string,
  relationTable: string,
  relatedId: string,
  id: string | undefined,
  translationKey: string,
) {
  if (!id) {
    throw new Error("Id is required");
  }
  const { data } = await api.delete(
    `back/api/related-fields/${relationBase}/${relationTable}/${relatedId}/${id}`,
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return data;
}

export const useDeleteRelation = (
  id: string | undefined,
  type: LinkableType,
  relationTable: RelationTable,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (idToDelete: string) =>
      deleteRelation(
        type,
        relationTable,
        idToDelete,
        id,
        t(`${relationTable}UpdatedToastDescription`),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [type, relationTable, id] });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", type],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

async function countAllRelatedFields(
  id: string | undefined,
  baseTable: string,
  relationTable: string[],
) {
  const { data } = await api.post(
    `back/api/related-fields/count/${id}/${baseTable}`,
    { relationTable },
  );
  return data;
}

export const useCountAllRelatedFields = (
  id: string | undefined,
  type: LinkableType,
  relationTable: string[],
) => {
  return useQuery({
    queryKey: ["countAllRelatedFields", type, id, relationTable],
    queryFn: () => countAllRelatedFields(id, type, relationTable),
    enabled: !!id,
  });
};

export const useCountRelatedFields = (
  id: string | undefined,
  type: LinkableType,
  relationTable: string[],
) => {
  return useQuery({
    queryKey: ["countRelatedFields", type, id, relationTable],
    queryFn: () => countAllRelatedFields(id, type, relationTable),
    enabled: !!id,
  });
};

async function getComplexRelationFields(
  id: string,
  relation: string,
  type: LinkableType,
) {
  const { data } = await api.get(
    `back/api/related-fields/${id}/${relation}/${type}/complex-links`,
  );
  return data;
}

export const useGetComplexRelationFields = (
  id: string,
  relation: string,
  type: LinkableType,
) => {
  return useQuery({
    queryKey: [type, "complexRelationFields", id, relation],
    queryFn: () => getComplexRelationFields(id, relation, type),
  });
};

interface CreateOrUpdateArtworkInAuctionDTO {
  id: string;
  oeuvresDansVentesDescriptionNumeroLot?: string;
  oeuvresDansVentesDescriptionReproduitPage?: string;
  oeuvresDansVentesDescriptionEstimationHaute?: string;
  oeuvresDansVentesDescriptionEstimationBasse?: string;
  oeuvresDansVentesDescriptionVendu?: boolean;
  oeuvresDansVentesDescriptionResultatNonListe?: boolean;
  oeuvresDansVentesDescriptionNonVenu?: boolean;
  oeuvresDansVentesDescriptionNonVendu?: boolean;
  oeuvresDansVentesDescriptionNonReproduit?: boolean;
  oeuvresDansVentesDescriptionRetire?: boolean;
  oeuvresDansVentesDescriptionMiseAPrix?: string;
  oeuvresDansVentesDescriptionPrixMarteau?: string;
  oeuvresDansVentesDescriptionPrixAvecFrais?: string;
  oeuvresDansVentesDescriptionNotes?: string;
  artworkId: string;
  auctionId: string;
}

async function createOrUpdateArtworkInAuction(
  data: CreateOrUpdateArtworkInAuctionDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/artwork-in-auction`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateArtworkInAuction = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateArtworkInAuctionDTO) => createOrUpdateArtworkInAuction(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "artworkInAuctions",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateArtworkInExhibitionDTO {
  id: string;
  oeuvresDansExpositionsDescriptionNumeroCatalogue?: string;
  oeuvresDansExpositionsNotes?: string;
  artworkId: string;
  exhibitionId: string;
}

async function createOrUpdateArtworkInExhibition(
  data: CreateOrUpdateArtworkInExhibitionDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/artwork-in-exhibition`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateArtworkInExhibition = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateArtworkInExhibitionDTO) => createOrUpdateArtworkInExhibition(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "artworkInExhibitions",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateArtworkInBibliographyDTO {
  id: string;
  oeuvresDansBibliographieDescriptionReproduitPage?: string;
  oeuvresDansBibliographieDescriptionReproduitPlanche?: string;
  oeuvresDansBibliographieDescriptionPlancheNonNumerotee?: boolean;
  oeuvresDansBibliographieDescriptionCitePage?: string;
  oeuvresDansBibliographieDescriptionListePage?: string;
  oeuvresDansBibliographieDescriptionSousNumero?: string;
  oeuvresDansBibliographieDescriptionPhotographe?: string;
  oeuvresDansBibliographieDescriptionReproductionCouleur?: boolean;
  oeuvresDansBibliographieDescriptionNonPagine?: boolean;
  oeuvresDansBibliographieDescriptionReproductionCouverture?: boolean;
  oeuvresDansBibliographieDescriptionTexteLibre?: string;
  oeuvresDansBibliographieNotes?: string;
  artworkId: string;
  bibliographyId: string;
}

async function createOrUpdateArtworkInBibliography(
  data: CreateOrUpdateArtworkInBibliographyDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/artwork-in-bibliography`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateArtworkInBibliography = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateArtworkInBibliographyDTO) => createOrUpdateArtworkInBibliography(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "artworkInBibliography",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateCollectorInMultipleprintDTO {
  id: string;
  estampesDansCollectionneursDescriptionInfos?: string;
  collectorId: string;
  multipleprintId: string;
}

async function createOrUpdateCollectorInMultipleprint(
  data: CreateOrUpdateCollectorInMultipleprintDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/collector-in-multipleprint`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateCollectorInMultipleprint = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateCollectorInMultipleprintDTO) => createOrUpdateCollectorInMultipleprint(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "collectorInMultipleprints",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateMultipleprintInBibliographyDTO {
  id: string;
  bibliographieDansEstampesMultiplesDescriptionReproduitPage?: string;
  bibliographieDansEstampesMultiplesDescriptionReproduitPlanche?: string;
  biblioInMultiPrintDescPlancheNonNum?: boolean;
  bibliographieDansEstampesMultiplesDescriptionCitePage?: string;
  bibliographieDansEstampesMultiplesDescriptionListePage?: string;
  bibliographieDansEstampesMultiplesDescriptionSousNumero?: string;
  bibliographieDansEstampesMultiplesDescriptionPhotographe?: string;
  bibliographieDansEstampesMultiplesDescriptionReproductionCoul?: boolean;
  biblioInMultiPrintDescReproCover?: boolean;
  bibliographieDansEstampesMultiplesDescriptionNonPagine?: boolean;
  bibliographieDansEstampesMultiplesDescriptionTexteLibre?: string;
  bibliographieDansEstampesMultiplesDescriptionExemplaireRepr?: string;
  multipleprintId: string;
  bibliographyId: string;
}

async function createOrUpdateMultipleprintInBibliography(
  data: CreateOrUpdateMultipleprintInBibliographyDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/multipleprint-in-bibliography`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateMultipleprintInBibliography = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateMultipleprintInBibliographyDTO) => createOrUpdateMultipleprintInBibliography(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "multipleprintInBibliography",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateMultipleprintInAuctionDTO {
  id: string;
  ventesDansEstampesMultiplesDescriptionNumeroLot?: string;
  ventesDansEstampesMultiplesDescriptionReproduitPage?: string;
  ventesDansEstampesMultiplesDescriptionEstimationHaute?: string;
  ventesDansEstampesMultiplesDescriptionEstimationBasse?: string;
  ventesDansEstampesMultiplesDescriptionVendu?: boolean;
  ventesDansEstampesMultiplesDescriptionResultatNonListe?: boolean;
  ventesDansEstampesMultiplesDescriptionNonVendu?: boolean;
  ventesDansEstampesMultiplesDescriptionNonVenu?: boolean;
  ventesDansEstampesMultiplesDescriptionRetire?: boolean;
  ventesDansEstampesMultiplesDescriptionMiseAPrix?: string;
  ventesDansEstampesMultiplesDescriptionPrixMarteau?: string;
  ventesDansEstampesMultiplesDescriptionPrixAvecFrais?: string;
  ventesDansEstampesMultiplesDescriptionExemplaireVendu?: string;
  multipleprintId: string;
  auctionId: string;
}

async function createOrUpdateMultipleprintInAuction(
  data: CreateOrUpdateMultipleprintInAuctionDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/multipleprint-in-auction`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateMultipleprintInAuction = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateMultipleprintInAuctionDTO) => createOrUpdateMultipleprintInAuction(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "multipleprintInAuction",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateMultipleprintInExhibitionDTO {
  id: string;
  expositionsDansEstampesMultiplesDescriptionNumero?: string;
  expositionsDansEstampesMultiplesDescriptionExemplaireExpose?: string;
  multipleprintId: string;
  exhibitionId: string;
}

async function createOrUpdateMultipleprintInExhibition(
  data: CreateOrUpdateMultipleprintInExhibitionDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/multipleprint-in-exhibition`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateMultipleprintInExhibition = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateMultipleprintInExhibitionDTO) => createOrUpdateMultipleprintInExhibition(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "multipleprintInExhibition",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateMultipleSampleInExhibitionDTO {
  id: string;
  multiplesampleDansExpositionsDescriptionNumeroCatalogue?: string;
  multiplesampleDansExpositionsNotes?: string;
  multiplesampleId: string;
  exhibitionId: string;
}

async function createOrUpdateMultipleSampleInExhibition(
  data: CreateOrUpdateMultipleSampleInExhibitionDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/multiplesample-in-exhibition`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateMultipleSampleInExhibition = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateMultipleSampleInExhibitionDTO) => createOrUpdateMultipleSampleInExhibition(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "multipleSampleInExhibitions",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateMultipleSampleInBibliographyDTO {
  id: string;
  multiplesampleDansBibliographieDescriptionReproduitPage?: string;
  multiplesampleDansBibliographieDescriptionReproduitPlanche?: string;
  multiplesampleDansBibliographieDescriptionPlancheNonNumerotee?: boolean;
  multiplesampleDansBibliographieDescriptionCitePage?: string;
  multiplesampleDansBibliographieDescriptionListePage?: string;
  multiplesampleDansBibliographieDescriptionSousNumero?: string;
  multiplesampleDansBibliographieDescriptionPhotographe?: string;
  multiplesampleDansBibliographieDescriptionReproductionCouleur?: boolean;
  multiSampleInBiblioDescReproCover?: boolean;
  multiplesampleDansBibliographieDescriptionNonPagine?: boolean;
  multiplesampleDansBibliographieDescriptionTexteLibre?: string;
  multiplesampleDansBibliographieNotes?: string;
  multiplesampleId: string;
  bibliographyId: string;
}

async function createOrUpdateMultipleSampleInBibliography(
  data: CreateOrUpdateMultipleSampleInBibliographyDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/multiplesample-in-bibliography`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateMultipleSampleInBibliography = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateMultipleSampleInBibliographyDTO) => createOrUpdateMultipleSampleInBibliography(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "multipleSampleInBibliography",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateTirageInExhibitionDTO {
  id: string;
  tirageDansExpositionsDescriptionNumeroCatalogue?: string;
  tirageDansExpositionsNotes?: string;
  tirageId: string;
  exhibitionId: string;
}

async function createOrUpdateTirageInExhibition(
  data: CreateOrUpdateTirageInExhibitionDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/tirage-in-exhibition`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateTirageInExhibition = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateTirageInExhibitionDTO) => createOrUpdateTirageInExhibition(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "tirageInExhibition",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateTirageInAuctionDTO {
  id: string;
  tirageDansVentesDescriptionNumeroLot?: string;
  tirageDansVentesDescriptionReproduitPage?: string;
  tirageDansVentesDescriptionEstimationHaute?: string;
  tirageDansVentesDescriptionEstimationBasse?: string;
  tirageDansVentesDescriptionVendu?: boolean;
  tirageDansVentesDescriptionResultatNonListe?: boolean;
  tirageDansVentesDescriptionNonVenu?: boolean;
  tirageDansVentesDescriptionNonVendu?: boolean;
  tirageDansVentesDescriptionNonReproduit?: boolean;
  tirageDansVentesDescriptionRetire?: boolean;
  tirageDansVentesDescriptionMiseAPrix?: string;
  tirageDansVentesDescriptionPrixMarteau?: string;
  tirageDansVentesDescriptionPrixAvecFrais?: string;
  tirageDansVentesDescriptionNotes?: string;
  tirageId: string;
  auctionId: string;
}

async function createOrUpdateTirageInAuction(
  data: CreateOrUpdateTirageInAuctionDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/tirage-in-auction`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateTirageInAuction = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateTirageInAuctionDTO) => createOrUpdateTirageInAuction(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "tirageInAuction",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateMultipleSampleInAuctionDTO {
  id: string;
  multiplesampleId: string;
  auctionId: string;
  multiplesampleDansVentesDescriptionNumeroLot?: string;
  multiplesampleDansVentesDescriptionReproduitPage?: string;
  multiplesampleDansVentesDescriptionEstimationHaute?: string;
  multiplesampleDansVentesDescriptionEstimationBasse?: string;
  multiplesampleDansVentesDescriptionVendu?: boolean;
  multiplesampleDansVentesDescriptionResultatNonListe?: boolean;
  multiplesampleDansVentesDescriptionNonVenu?: boolean;
  multiplesampleDansVentesDescriptionNonVendu?: boolean;
  multiplesampleDansVentesDescriptionNonReproduit?: boolean;
  multiplesampleDansVentesDescriptionRetire?: boolean;
  multiplesampleDansVentesDescriptionMiseAPrix?: string;
  multiplesampleDansVentesDescriptionPrixMarteau?: string;
  multiplesampleDansVentesDescriptionPrixAvecFrais?: string;
  multiplesampleDansVentesDescriptionNotes?: string;
}

async function createOrUpdateMultipleSampleInAuction(
  data: CreateOrUpdateMultipleSampleInAuctionDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    "back/api/related-fields/multiplesample-in-auction",
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateMultipleSampleInAuction = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateMultipleSampleInAuctionDTO) =>
      createOrUpdateMultipleSampleInAuction(
        data,
        t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "multipleSampleInAuction",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateSculptureEditionInExhibitionDTO {
  id: string;
  sculptureEditionId: string;
  exhibitionId: string;
  sculptureEditionDansExpositionsNumeroCatalogue?: string;
  sculptureEditionDansExpositionsNotes?: string;
}

export const useCreateOrUpdateSculptureEditionInExhibition = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (data: CreateOrUpdateSculptureEditionInExhibitionDTO) => {
      const res = await api.post("back/api/related-fields/sculpture-edition-in-exhibition", { data });
      toast({ title: t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`), duration: 2000, variant: "success" });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [relationBase, "complexRelationFields", id, "sculptureEditionInExhibitions"] });
      queryClient.invalidateQueries({ queryKey: ["countAllRelatedFields", relationBase] });
      queryClient.invalidateQueries({ queryKey: ["not-related"] });
    },
  });
};

interface CreateOrUpdateSculptureEditionInBibliographyDTO {
  id: string;
  sculptureEditionId: string;
  bibliographyId: string;
  sculptureEditionDansBibliographieReproduitPage?: string;
  sculptureEditionDansBibliographieReproduitPlanche?: string;
  sculptureEditionDansBibliographiePlancheNonNumerotee?: boolean;
  sculptureEditionDansBibliographieCitePage?: string;
  sculptureEditionDansBibliographieListePage?: string;
  sculptureEditionDansBibliographieSousNumero?: string;
  sculptureEditionDansBibliographiePhotographe?: string;
  sculptureEditionDansBibliographieNonPagine?: boolean;
  sculptureEditionDansBibliographieReproductionCouverture?: boolean;
  sculptureEditionDansBibliographieReproductionCouleur?: boolean;
  sculptureEditionDansBibliographieTexteLibre?: string;
  sculptureEditionDansBibliographieNotes?: string;
}

export const useCreateOrUpdateSculptureEditionInBibliography = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (data: CreateOrUpdateSculptureEditionInBibliographyDTO) => {
      const res = await api.post("back/api/related-fields/sculpture-edition-in-bibliography", { data });
      toast({ title: t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`), duration: 2000, variant: "success" });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [relationBase, "complexRelationFields", id, "sculptureEditionInBibliography"] });
      queryClient.invalidateQueries({ queryKey: ["countAllRelatedFields", relationBase] });
      queryClient.invalidateQueries({ queryKey: ["not-related"] });
    },
  });
};

interface CreateOrUpdateSculptureEditionInAuctionDTO {
  id: string;
  sculptureEditionId: string;
  auctionId: string;
  sculptureEditionDansVentesNumeroLot?: string;
  sculptureEditionDansVentesReproduitPage?: string;
  sculptureEditionDansVentesEstimationHaute?: string;
  sculptureEditionDansVentesEstimationBasse?: string;
  sculptureEditionDansVentesVendu?: boolean;
  sculptureEditionDansVentesResultatNonListe?: boolean;
  sculptureEditionDansVentesNonVenu?: boolean;
  sculptureEditionDansVentesNonVendu?: boolean;
  sculptureEditionDansVentesNonReproduit?: boolean;
  sculptureEditionDansVentesRetire?: boolean;
  sculptureEditionDansVentesMiseAPrix?: string;
  sculptureEditionDansVentesPrixMarteau?: string;
  sculptureEditionDansVentesPrixAvecFrais?: string;
  sculptureEditionDansVentesNotes?: string;
}

export const useCreateOrUpdateSculptureEditionInAuction = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (data: CreateOrUpdateSculptureEditionInAuctionDTO) => {
      const res = await api.post("back/api/related-fields/sculpture-edition-in-auction", { data });
      toast({ title: t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`), duration: 2000, variant: "success" });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [relationBase, "complexRelationFields", id, "sculptureEditionInAuction"] });
      queryClient.invalidateQueries({ queryKey: ["countAllRelatedFields", relationBase] });
      queryClient.invalidateQueries({ queryKey: ["not-related"] });
    },
  });
};

interface CreateOrUpdateTirageInBibliographyDTO {
  id: string;
  tirageDansBibliographieDescriptionReproduitPage?: string;
  tirageDansBibliographieDescriptionReproduitPlanche?: string;
  tirageDansBibliographieDescriptionPlancheNonNumerotee?: boolean;
  tirageDansBibliographieDescriptionCitePage?: string;
  tirageDansBibliographieDescriptionListePage?: string;
  tirageDansBibliographieDescriptionSousNumero?: string;
  tirageDansBibliographieDescriptionPhotographe?: string;
  tirageDansBibliographieDescriptionReproductionCouleur?: boolean;
  tirageDansBibliographieDescriptionReproductionCouverture?: boolean;
  tirageDansBibliographieDescriptionNonPagine?: boolean;
  tirageDansBibliographieDescriptionTexteLibre?: string;
  tirageDansBibliographieNotes?: string;
  tirageId: string;
  bibliographyId: string;
}

async function createOrUpdateTirageInBibliography(
  data: CreateOrUpdateTirageInBibliographyDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/tirage-in-bibliography`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateTirageInBibliography = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateTirageInBibliographyDTO) => createOrUpdateTirageInBibliography(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "tirageInBibliography",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateDocumentInBibliographyDTO {
  id: string;
  documentsDansBibliographieDescriptionReproduitPage?: string;
  documentsDansBibliographieDescriptionCitePage?: string;
  documentsDansBibliographieDescriptionPhotographe?: string;
  documentsDansBibliographieDescriptionReproductionCouleur?: boolean;
  documentsDansBibliographieDescriptionNonPagine?: boolean;
  documentId: string;
  bibliographyId: string;
}

async function createOrUpdateDocumentInBibliography(
  data: CreateOrUpdateDocumentInBibliographyDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/document-in-bibliography`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateDocumentInBibliography = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateDocumentInBibliographyDTO) => createOrUpdateDocumentInBibliography(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "documentInBibliography",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdateBibliographyInBibliographyDTO {
  id: string;
  bibliographieDansBibliographieDescriptionPage?: string;
  bibliographieDansBibliographieDescriptionReproduitPage?: string;
  bibliographieDansBibliographieDescriptionCitePage?: string;
  bibliographieDansBibliographieDescriptionListePage?: string;
  bibliographieDansBibliographieDescriptionNonPagine?: boolean;
  bibliographieDansBibliographieDescriptionReproductionCouverture?: boolean;
  bibliographieDansBibliographieDescriptionReproductionCouleur?: boolean;
  bibliographieDansBibliographieDescriptionTextePersonnalise?: string;
  bibliographyAId: string;
  bibliographyBId: string;
}

async function createOrUpdateBibliographyInBibliography(
  data: CreateOrUpdateBibliographyInBibliographyDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/bibliography-in-bibliography`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdateBibliographyInBibliography = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdateBibliographyInBibliographyDTO) =>
      createOrUpdateBibliographyInBibliography(
        data,
        t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "bibliographyInBibliography",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface CreateOrUpdatePhotographyInBibliographyDTO {
  id: string;
  photographiesDansBibliographieDescriptionReproduitPage?: string;
  photographiesDansBibliographieDescriptionCitePage?: string;
  photographiesDansBibliographieDescriptionPhotographe?: string;
  photographiesDansBibliographieDescriptionReproductionCouleur?: boolean;
  photographiesDansBibliographieDescriptionNonPagine?: boolean;
  photographyId: string;
  bibliographyId: string;
}

async function createOrUpdatePhotographyInBibliography(
  data: CreateOrUpdatePhotographyInBibliographyDTO,
  translationKey: string,
) {
  const { data: result } = await api.post(
    `back/api/related-fields/photography-in-bibliography`,
    { data },
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return result;
}

export const useCreateOrUpdatePhotographyInBibliography = (
  id: string,
  relationBase: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateOrUpdatePhotographyInBibliographyDTO) => createOrUpdatePhotographyInBibliography(data, t(`${relationBase}ComplexRelationFieldsUpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          relationBase,
          "complexRelationFields",
          id,
          "photographyInBibliography",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", relationBase],
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
      });
    },
  });
};

interface DeleteComplexLinkDTO {
  id: string;
  relation: string;
}

async function deleteComplexLink(deleteComplexLinkDTO: DeleteComplexLinkDTO, translationKey: string) {
  const { data } = await api.delete(
    `back/api/related-fields/${deleteComplexLinkDTO.id}/${deleteComplexLinkDTO.relation}/complex-link`,
  );
  toast({
    title: translationKey,
    duration: 2000,
    variant: "success",
  });
  return data;
}

export const useDeleteComplexLink = (
  id: string,
  relation: string,
  type: LinkableType,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: DeleteComplexLinkDTO) => deleteComplexLink(data, t(`${relation}UpdatedToastDescription`)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [type, "complexRelationFields", id],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["countAllRelatedFields", type],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["not-related"],
        exact: false,
      });
    },
  });
};

/** What we send for `associationScope` (backend default is full when omitted). */
type NotRelatedAssociationScopeParam = "relatedMultiplesample" | "portfolioItems";
type NotRelatedAssociationScope = "full" | NotRelatedAssociationScopeParam;

function resolveNotRelatedAssociationScope(
  resultType: ResourceType,
  explicit: NotRelatedAssociationScope | undefined,
  anchorResourceType: LinkableType | undefined,
): NotRelatedAssociationScopeParam | undefined {
  if (explicit === "relatedMultiplesample" || explicit === "portfolioItems")
    return explicit;
  if (explicit === "full") return undefined;
  if (
    resultType === "multiplesample" &&
    anchorResourceType === "multiplesample"
  ) {
    return "relatedMultiplesample";
  }
  return undefined;
}

export const getNotRelatedResource = async <T>(input: {
  resultType: ResourceType;
  pagination: PagingParameters;
  resourceId: string;
  searchQuery?: string;
  /**
   * Override du scope d’associations pour l’API. Sinon, utiliser
   * `anchorResourceType` (recommandé pour les listes d’exemplaires).
   */
  associationScope?: NotRelatedAssociationScope;
  /**
   * Type de la fiche sur laquelle on se trouve. Pour une fiche exemplaire,
   * les listes `resultType: multiplesample` excluent soi-même et les exemplaires
   * déjà liés (peer) via `associationScope=relatedMultiplesample`.
   */
  anchorResourceType?: LinkableType;
  /** `resultType: multiple` uniquement : ne proposer que cette nature (onglet Portfolio → simple). */
  multipleprintNature?: MultipleprintNature;
}) => {
  const associationScope = resolveNotRelatedAssociationScope(
    input.resultType,
    input.associationScope,
    input.anchorResourceType,
  );
  const { data } = await api.get<PaginatedReturn<T>>(
    `back/api/related-fields/${input.resultType}/${input.resourceId}/not-related`,
    {
      params: {
        ...input.pagination,
        search: input.searchQuery,
        ...(associationScope
          ? { associationScope }
          : {}),
        ...(input.multipleprintNature
          ? { multipleprintNature: input.multipleprintNature }
          : {}),
      },
    },
  );
  return data;
};

export const useGetNotRelatedResource = <T>({
  resultType,
  resourceId,
  pagination,
  searchQuery,
  associationScope,
  anchorResourceType,
  multipleprintNature,
}: {
  resultType: ResourceType;
  resourceId: string;
  pagination: PagingParameters;
  searchQuery?: string;
  associationScope?: NotRelatedAssociationScope;
  /** Voir `getNotRelatedResource` — à fournir pour les listes d’exemplaires selon le contexte. */
  anchorResourceType?: LinkableType;
  multipleprintNature?: MultipleprintNature;
}) => {
  return useQuery({
    queryKey: [
      "not-related",
      resultType,
      resourceId,
      pagination,
      searchQuery,
      associationScope,
      anchorResourceType,
      multipleprintNature,
    ],
    placeholderData: (data?: PaginatedReturn<T>) => data,
    queryFn: () =>
      getNotRelatedResource<T>({
        resultType,
        resourceId,
        pagination,
        searchQuery,
        associationScope,
        anchorResourceType,
        multipleprintNature,
      }),
  });
};
